/** eslint-disable max-len */

import * as yup from "yup";
import "yup-phone";
import { Rule, RuleObject } from "rc-field-form/lib/interface";
import { AnyObject } from "@triare/auth-redux";
import { isValidPhoneNumber } from "libphonenumber-js";

export const textField: yup.StringSchema = yup
  .string()
  .min(3, "Must be at least 3 characters")
  .max(100, "Cannot be more than 100 characters.")
  .required("Required");

export const name: yup.StringSchema = yup
  .string()
  .min(3, "Must be at least 3 characters")
  .max(50, "Cannot be more than 50 characters.")
  .required("Required");

export const username: yup.StringSchema = yup
  .string()
  .min(6, "Must be at least 6 characters")
  .max(100, "Cannot be more than 100 characters.")
  .required("Required");

export const password: yup.StringSchema = yup
  .string()
  .min(12, "Password must be at least 12 characters")
  .max(100, "Too Long! Maximum 100 characters")
  .matches(/^(?=.*\d)(?=.*\D)([\D\d]+)$/, "Must contain numbers and letters")
  .matches(/[A-Z]/, "Must contain one uppercase letter")
  .matches(/[^\dA-Za-z]/, "Must contain one special symbol")
  .matches(/[a-z]/, "Must contain one lowercase letter")
  .matches(/^\S+$/, "Should not contain whitespaces")
  .required("Required");

export const confirmPassword: yup.StringSchema = yup
  .string()
  .required("Required");

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// export const phone: yup.StringSchema = yup.string().phone()
//   .max(15, 'Please enter a valid phone number.')
//   .required('Required');

export const phone = yup
  .string()
  .test("is-valid-phone", "Please enter a valid phone number.", (value) =>
    isValidPhoneNumber(value || ""),
  )
  .max(15, "Please enter a valid phone number.")
  .required("Required");

export const email: yup.StringSchema = yup
  .string()
  .email("Please enter a valid email address.")
  .required("Required")
  .matches(/^.+.\w{2}$/, "At least two characters after the period");

export const confirmEmail: yup.StringSchema = yup
  .string()
  .oneOf([yup.ref("email")], "Email must match")
  .required("Required");

export const zipCode: yup.StringSchema = yup
  .string()
  .min(5, "Please enter a valid Zip Code.")
  .max(10, "Please enter a valid Zip Code.")
  .matches(/^\d{5}$|^\d{5}-\d{4}$/, "Please enter a valid Zip Code.")
  .required("Required");

export const validationSignUpEmailOnly = yup.object().shape({
  email,
});

export const validationSignUpEmail = yup.object().shape({
  email,
});

export const validationSignUpEmailAndPassword = yup.object({
  email,
  password,
  confirmPassword,
});

export const validationSignUpPhoneOnly = yup.object().shape({
  phone,
});

export const validationSignUpPhoneAndPasswordOnly = yup.object().shape({
  phone,
  password,
  confirmPassword,
});

export const validationSignUp = yup.object().shape({
  password,
  email,
  phone,
  firstName: name,
  lastName: name,
  address: name,
});

export const validationSignUpChangeEmail = yup.object().shape({
  email,
});

export const validationGeneralInfo = yup.object().shape({
  name,
  address: textField,
  phone,
  email,
});

export const validationUserInfo = yup.object().shape({
  firstName: name,
  lastName: name,
  phone,
  email,
});

export const validationChangePassword = yup.object().shape({
  currentPassword: password,
  password,
});

export const validationNewPassword = yup.object().shape({
  password,
});

export const validationChangeEmail = yup.object().shape({
  email,
  confirmEmail,
});

export const validationUserAdd = yup.object().shape({
  lastName: textField,
  firstName: textField,
  phone,
  email,
});

export const validationApplicant = yup.object().shape({
  indemnityNumber: name,
  ahpra: yup
    .string()
    .min(5, "Must be at least 5 characters")
    .max(15, "Cannot be more than 15 characters.")
    .required("Required"),
  address: textField,
  lastName: textField,
  firstName: textField,
  phone,
  email,
});

export const validationSignInEmailAndPassword = yup.object().shape({
  email,
  password,
});

export const validationSignInUsernameAndPassword = yup.object().shape({
  username,
  password,
});

export const validationSignInPhoneOnly = yup.object().shape({
  phone,
});

export const validationSignInPhoneAndPassword = yup.object().shape({
  phone,
  password,
});

export const validationForgotPassword = yup.object().shape({
  email,
});

const { shape } = yup.object();

export const createRulesForAntd = (
  yupRule: ReturnType<typeof shape>,
  getValue?: () => AnyObject,
): Rule => ({
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  async validator({ field }: RuleObject, value) {
    if (field.includes(".") && getValue) {
      await yupRule.validateSyncAt(field, getValue());
    } else {
      await yupRule.validateSyncAt(field, { [field]: value });
    }
  },
});

const validatorRules: { [key: string]: Rule } = {};

export const getRulesForAntd = (yupRule: ReturnType<typeof shape>): Rule => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (validatorRules[yupRule.name]) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return validatorRules[yupRule.name];
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  validatorRules[yupRule.name] = createRulesForAntd(yupRule);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return validatorRules[yupRule.name];
};

export default {
  validationSignUp,
  validationChangePassword,
  validationNewPassword,
  validationChangeEmail,
  validationSignIn: validationSignInEmailAndPassword,
  validationForgotPassword,
  password,
  email,
  confirmEmail,
};
